import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../ui/Icon';

const ProcessingView = ({ fileName, toolName, onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('uploading');
  const [done, setDone] = useState(false);

  const stages = {
    uploading: { label: 'Uploading file...', icon: 'Upload', color: '#3374ff' },
    analyzing: { label: 'Analyzing document...', icon: 'Eye', color: '#8b5cf6' },
    processing: { label: 'Processing conversion...', icon: 'Zap', color: '#f9603e' },
    finalizing: { label: 'Finalizing output...', icon: 'Sparkles', color: '#10b981' },
    complete: { label: 'Complete!', icon: 'Check', color: '#10b981' },
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(interval);
          setStage('complete');
          setDone(true);
          onComplete?.();
          return 100;
        }
        if (p < 20) setStage('uploading');
        else if (p < 50) setStage('analyzing');
        else if (p < 85) setStage('processing');
        else setStage('finalizing');
        return p + Math.random() * 4 + 1;
      });
    }, 120);
    return () => clearInterval(interval);
  }, [onComplete]);

  const currentStage = stages[stage];

  return (
    <div className="max-w-lg mx-auto py-12">
      {/* Animated Icon */}
      <div className="flex justify-center mb-8">
        <motion.div
          animate={done ? { scale: [1, 1.2, 1] } : { rotate: [0, 360] }}
          transition={done ? { duration: 0.5 } : { duration: 2, repeat: Infinity, ease: 'linear' }}
          className="w-20 h-20 rounded-2xl flex items-center justify-center"
          style={{ background: `${currentStage.color}15` }}
        >
          <Icon name={currentStage.icon} size={36} style={{ color: currentStage.color }} />
        </motion.div>
      </div>

      {/* File Info */}
      <div className="text-center mb-8">
        <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
          {done ? 'Conversion Complete!' : `Converting ${fileName || 'your file'}`}
        </h3>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {currentStage.label}
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
          <span>{toolName || 'Processing'}</span>
          <span>{Math.min(Math.round(progress), 100)}%</span>
        </div>
        <div className="h-3 rounded-full overflow-hidden" style={{ background: 'var(--bg-tertiary)' }}>
          <motion.div
            className="h-full rounded-full"
            style={{
              width: `${Math.min(progress, 100)}%`,
              background: `linear-gradient(90deg, ${currentStage.color}, ${currentStage.color}cc)`,
              boxShadow: `0 0 16px ${currentStage.color}40`,
            }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Stage Steps */}
      <div className="flex items-center justify-center gap-2">
        {Object.entries(stages).slice(0, 4).map(([key, s], i) => {
          const stageOrder = ['uploading', 'analyzing', 'processing', 'finalizing'];
          const currentIdx = stageOrder.indexOf(stage);
          const thisIdx = i;
          const isActive = thisIdx === currentIdx;
          const isComplete = thisIdx < currentIdx || done;
          return (
            <React.Fragment key={key}>
              <div className="flex items-center gap-1.5">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                  isComplete ? 'text-white' : isActive ? 'text-white' : ''
                }`} style={{
                  background: isComplete ? '#10b981' : isActive ? s.color : 'var(--bg-tertiary)',
                  color: isComplete || isActive ? 'white' : 'var(--text-muted)',
                }}>
                  {isComplete ? <Icon name="Check" size={12} /> : i + 1}
                </div>
              </div>
              {i < 3 && (
                <div className="w-8 h-0.5 rounded-full transition-colors duration-300"
                  style={{ background: isComplete ? '#10b981' : 'var(--border-light)' }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Action Buttons (shown when complete) */}
      {done && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button className="btn-primary !px-8">
            <Icon name="Download" size={16} className="mr-2" />
            Download File
          </button>
          <button className="btn-secondary flex items-center gap-2">
            <Icon name="Cloud" size={16} />
            Save to Drive
          </button>
          <button className="btn-secondary flex items-center gap-2">
            <Icon name="Mail" size={16} />
            Email
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default ProcessingView;
